import React, { useState } from 'react';
import './ContactForm.css';
import lukaszContactPhoto from '../assets/images/lukasz.png';

interface ContactFormProps {
  title?: string;
  subtitle?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  title = "Zapisz się na zajęcia",
  subtitle = "Wyślij nam swoje zgłoszenie a my skontaktujemy się z Tobą!"
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: '',
    interestedInEnglish: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Make.com webhook URL
      const webhookUrl = process.env.REACT_APP_WEBHOOK_URL || 'https://hook.eu2.make.com/ffvazoekvw2n7uee1x725bqhdsdeegf1';

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          source: 'Sigma Study Contact Form',
          data: formData
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          course: '',
          message: '',
          interestedInEnglish: false
        });
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Webhook submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-section">
      <div className="section-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <div className="contact-content">
        <div className="contact-form-container">
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Imię i nazwisko *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Wpisz swoje imię i nazwisko"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="twoj@email.pl"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefon *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="+48 532 533 220"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="course">Interesujący kurs *</label>
                <select
                  id="course"
                  name="course"
                  value={formData.course}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Wybierz kurs</option>
                  <option value="matura-podstawowa">Matura Podstawowa</option>
                  <option value="matura-rozszerzona">Matura Rozszerzona</option>
                  <option value="egzamin-osmoklasisty">Egzamin Ósmoklasisty</option>
                  <option value="matematyka-podstawowa">Szkoła Podstawowa</option>
                  <option value="konkursy-olimpiady">Konkursy i Olimpiady</option>
                  <option value="egzaminy-zagraniczne">Egzaminy Zagraniczne</option>
                  <option value="indywidualne">Indywidualne Potrzeby</option>
                  <option value="nadrabianie">Nadrabianie Zaległości</option>
                </select>
              </div>

            </div>

            <div className="form-group">
              <label htmlFor="message">Dodatkowe informacje</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                placeholder="Opisz swoje cele, oczekiwania lub zadaj pytanie..."
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="interestedInEnglish"
                  checked={formData.interestedInEnglish}
                  onChange={handleInputChange}
                />
                <span>Chcę otrzymać też ofertę zajęć z angielskiego </span>
              </label>
            </div>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? '⏳ Wysyłanie...' : 'Wyślij zgłoszenie'}
            </button>

            {submitStatus === 'success' && (
              <div className="status-message success">
                ✅ Dziękujemy za zainteresowanie! Skontaktujemy się z Tobą w ciągu 24 godzin .
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="status-message error">
                ❌ Wystąpił błąd podczas wysyłania. Spróbuj ponownie lub zadzwoń: +48 532 533 220
              </div>
            )}

            <p className="form-note">
              * Pola wymagane. Pierwsza lekcja GRATIS!
            </p>
          </form>
        </div>

        <div className="contact-photo-container">
          <div className="contact-photo-large">
            <img src={lukaszContactPhoto} alt="Łukasz - Założyciel Sigma Study" className="contact-large-image" loading="eager" />
            <div className="contact-photo-info">

              <div className="contact-phone-large">
                <svg className="phone-icon" viewBox="0 0 512 512" fill="#EF4444" style={{ width: '1em', height: '1em' }}>
                  <path d="M497.39 361.8l-112-48a24 24 0 0 0-28 6.9l-49.6 60.6A370.66 370.66 0 0 1 130.6 204.11l60.6-49.6a23.94 23.94 0 0 0 6.9-28l-48-112A24.16 24.16 0 0 0 122.6.61l-104 24A24 24 0 0 0 0 48c0 256.5 207.9 464 464 464a24 24 0 0 0 23.4-18.6l24-104a24.29 24.29 0 0 0-14.01-27.6z"/>
                </svg>
                <span className="phone-number">+48 532 533 220</span>
              </div>

              {/*<div className="social-media-section">*/}
              {/*  <h4>Nasze social media</h4>*/}
              {/*  <div className="social-links">*/}
              {/*    <SocialIcon url="https://www.instagram.com/sigma_study_/" style={{ height: 35, width: 35 }} />*/}
              {/*    <SocialIcon url="https://www.facebook.com/people/Sigma-Study/61576967721817/" style={{ height: 35, width: 35 }} />*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
